import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, Phone, Loader2 } from "lucide-react";
import { CONTACT, ACCESS_OPTIONS, SIZE_OPTIONS, SERVICE_OPTIONS, whatsappLink } from "@/data/content";
import Select from "@/components/Select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 transition-colors duration-150 focus:border-amber-500";

const Field = ({ label, required, children, testid }) => (
  <label className="block space-y-1.5" data-testid={testid}>
    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
      {label} {required && <span className="text-amber-600">*</span>}
    </span>
    {children}
  </label>
);

export default function QuoteForm() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    pickup: params.get("from") || "", pickup_access: ACCESS_OPTIONS[0],
    dropoff: params.get("to") || "", dropoff_access: ACCESS_OPTIONS[0],
    move_date: "", property_size: params.get("size") || SIZE_OPTIONS[0],
    services: ["Furniture Moving"], notes: "", website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));

  const waMessage = () =>
    whatsappLink(
      `Hi NextMove, I just submitted a quote request.\nName: ${form.name}\nMove: ${form.pickup} → ${form.dropoff}\nDate: ${form.move_date}\nSize: ${form.property_size}\nServices: ${form.services.join(", ")}`
    );

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quote`, form);
      setDone(res.data);
      toast.success("Quote request received, we'll be in touch shortly.");
      window.scrollTo({ top: 0 });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call us on 1300 429 532.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success-panel" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 sm:p-12 text-center space-y-6">
        <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
        <div>
          <h3 className="font-display text-2xl font-black text-navy">Got it, {form.name.split(" ")[0]}.</h3>
          <p className="mt-2 text-slate-600">
            Your quote request is in our inbox and safely stored. We reply fast during business hours
            ({CONTACT.hours}), or skip the queue:
          </p>
        </div>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <a href={waMessage()} target="_blank" rel="noopener noreferrer" data-testid="quote-whatsapp-continue"
            className="cta-gleam inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white">
            <MessageCircle className="h-4 w-4" /> Continue on WhatsApp
          </a>
          <a href={`tel:${CONTACT.phonePrimaryRaw}`} data-testid="quote-call-continue"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-navy px-6 py-3 text-sm font-bold text-navy">
            <Phone className="h-4 w-4" /> Call {CONTACT.phonePrimary}
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm space-y-6">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full Name" required testid="field-name">
          <input data-testid="input-name" required className={inputCls} placeholder="e.g. Lachlan Smith"
            value={form.name} onChange={set("name")} />
        </Field>
        <Field label="Phone Number" required testid="field-phone">
          <input data-testid="input-phone" required type="tel" className={inputCls} placeholder="0426 382 669"
            value={form.phone} onChange={set("phone")} />
        </Field>
        <Field label="Email Address" required testid="field-email">
          <input data-testid="input-email" required type="email" className={inputCls} placeholder="lachlan@example.com.au"
            value={form.email} onChange={set("email")} />
        </Field>
        <Field label="Preferred Moving Date" required testid="field-date">
          <input data-testid="input-move-date" required type="date" className={inputCls}
            value={form.move_date} onChange={set("move_date")} />
        </Field>
        <Field label="Pickup Suburb & Postcode" required testid="field-pickup">
          <input data-testid="input-pickup" required className={inputCls} placeholder="e.g. Surry Hills NSW 2010"
            value={form.pickup} onChange={set("pickup")} />
        </Field>
        <Field label="Pickup Property Access" testid="field-pickup-access">
          <Select data-testid="select-pickup-access" className={inputCls} value={form.pickup_access} onChange={set("pickup_access")}>
            {ACCESS_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </Select>
        </Field>
        <Field label="Drop-off Suburb & Postcode" required testid="field-dropoff">
          <input data-testid="input-dropoff" required className={inputCls} placeholder="e.g. Richmond VIC 3121"
            value={form.dropoff} onChange={set("dropoff")} />
        </Field>
        <Field label="Drop-off Property Access" testid="field-dropoff-access">
          <Select data-testid="select-dropoff-access" className={inputCls} value={form.dropoff_access} onChange={set("dropoff_access")}>
            {ACCESS_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </Select>
        </Field>
      </div>

      <Field label="Move Size" required testid="field-size">
        <Select data-testid="select-property-size" className={inputCls} value={form.property_size} onChange={set("property_size")}>
          {SIZE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
        </Select>
      </Field>

      <div data-testid="field-services">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Services Required</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {SERVICE_OPTIONS.map((s) => {
            const active = form.services.includes(s);
            return (
              <button type="button" key={s} onClick={() => toggleService(s)}
                data-testid={`service-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                aria-pressed={active}
                className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-150 ${
                  active ? "border-navy bg-navy text-amber-400" : "border-slate-300 bg-white text-slate-600 hover:border-amber-500"
                }`}>
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <Field label="Special Notes or Heavy Items" testid="field-notes">
        <textarea data-testid="input-notes" rows={4} className={inputCls}
          placeholder="Tell us about bulky items, parking permits, lift booking times, or fragile heirlooms..."
          value={form.notes} onChange={set("notes")} />
      </Field>

      {/* Honeypot, hidden from humans, bots fill it */}
      <input type="text" name="website" value={form.website} onChange={set("website")}
        tabIndex={-1} autoComplete="off" aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }} />

      <button type="submit" disabled={submitting} data-testid="quote-form-submit-button"
        className="cta-gleam flex w-full items-center justify-center gap-2 rounded-full bg-navy py-4 text-base font-bold text-amber-400 disabled:opacity-60">
        {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
        {submitting ? "Sending…" : "Get My Free Quote"}
      </button>
      <p className="text-center text-xs text-slate-400">
        No spam, no obligation. Your details go straight to our quoting team, nothing else.
      </p>
    </form>
  );
}
